import { ComponentRef, EventEmitter, ViewContainerRef } from '@angular/core';
import {
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
  SetNewValueObject,
} from '@vi-iot/client';
import { VddStartStopComponent } from '../../vdd-start-stop/vdd-start-stop.component';
import { DiagnosticsHelpers } from '../../../diagnostics-helpers';
import { VddIpAddressEditComponent } from '../../vdd-ip-address-edit/vdd-ip-address-edit.component';
import { VddIpAddressReadonlyComponent } from '../../vdd-ip-address-readonly/vdd-ip-address-readonly.component';
import { VddMacAddressEditComponent } from '../../vdd-mac-address-edit/vdd-mac-address-edit.component';
import { VddMacAddressReadonlyComponent } from '../../vdd-mac-address-readonly/vdd-mac-address-readonly.component';
import { VddNumberEditComponent } from '../../vdd-number-edit/vdd-number-edit.component';
import { DiagnosticsFormGroupService } from '../../diagnostics-form-group.service';
import { VddNumberReadonlyComponent } from '../../vdd-number-readonly/vdd-number-readonly.component';
import { SemanticValues } from '../../../diagnostics-constants';
import { VddBooleanChoiceComponent } from '../../vdd-boolean-choice/vdd-boolean-choice.component';
import { VddEnumEditComponent } from '../../vdd-enum-edit/vdd-enum-edit.component';
import { VddEnumReadonlyComponent } from '../../vdd-enum-readonly/vdd-enum-readonly.component';
import { VddTextEditComponent } from '../../vdd-text-edit/vdd-text-edit.component';
import { VddTextReadonlyComponent } from '../../vdd-text-readonly/vdd-text-readonly.component';
import { VddDateEditComponent } from '../../vdd-date-edit/vdd-date-edit.component';
import { VddDateReadonlyComponent } from '../../vdd-date-readonly/vdd-date-readonly.component';
import { VddVinComponent } from '../../vdd-vin/vdd-vin.component';
import { VddSensorComponent } from '../../vdd-sensor/vdd-sensor.component';

export function createDiagnosticsVinComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  diagnosticsFormGroupService: DiagnosticsFormGroupService,
  setNewValueEmitter: EventEmitter<SetNewValueObject>,
  matchedValue: RemoteDiagnosticsVddGenericStructure,
  vddName: string
): void {
  const newComponentRef = structureContainer.createComponent(VddVinComponent);
  newComponentRef.instance.matchedValue = matchedValue;
  newComponentRef.instance.isVddEditable = isVddEditable;
  newComponentRef.instance.diagnosticsFormGroupService = diagnosticsFormGroupService;
  newComponentRef.instance.setNewValue = setNewValueEmitter;
  newComponentRef.instance.vddName = vddName;
}

export function createSensorComponent(
  structureContainer: ViewContainerRef,
  matchedValue: RemoteDiagnosticsVddGenericStructure
): void {
  const newComponentRef = structureContainer.createComponent(VddSensorComponent);
  newComponentRef.instance.matchedValue = matchedValue;
}

export function createVddStartStopComponent(
  structureContainer: ViewContainerRef,
  rawSingleRead: RemoteDiagnosticsSuccessResponseData,
  isVddEditable: boolean,
  parameterId: number,
  allMatchedValues: RemoteDiagnosticsVddGenericStructure[],
  setRawSingleReadValuesEventEmitter: EventEmitter<{
    rawSingleReadValues: RemoteDiagnosticsSuccessResponseData;
  }>
): void {
  const newComponentRef = structureContainer.createComponent(VddStartStopComponent);
  newComponentRef.instance.isVddEditable = isVddEditable;
  newComponentRef.instance.parameterId = parameterId;
  newComponentRef.instance.entriesStructure = DiagnosticsHelpers.getEntriesStructure(rawSingleRead);
  newComponentRef.instance.matchedValues = allMatchedValues;
  newComponentRef.instance.rawSingleReadValues = rawSingleRead;
  newComponentRef.instance.startStopTempStructure = DiagnosticsHelpers.getStartStopTempStructure(
    rawSingleRead
  );
  newComponentRef.instance.setRawSingleReadValuesEventEmitter = setRawSingleReadValuesEventEmitter;
}

export function createDiagnosticsVddDateComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  skipIndexes: number[],
  index: number,
  allMatchedValues: RemoteDiagnosticsVddGenericStructure[],
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  let newComponentRef: ComponentRef<VddDateEditComponent> | ComponentRef<VddDateReadonlyComponent>;
  if (isVddEditable) {
    newComponentRef = structureContainer.createComponent(VddDateEditComponent);
    newComponentRef.instance.setNewValue = setNewValueEmitter;
  } else {
    newComponentRef = structureContainer.createComponent(VddDateReadonlyComponent);
  }
  newComponentRef.instance.index = index;
  newComponentRef.instance.matchedValues = allMatchedValues;
  newComponentRef.instance.skipIndexes = skipIndexes;
}

export function createDiagnosticsVddIpAddressComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  index: number,
  allMatchedValues: RemoteDiagnosticsVddGenericStructure[],
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  if (isVddEditable) {
    const newComponentRef = structureContainer.createComponent(VddIpAddressEditComponent);
    newComponentRef.instance.structureArray = getArrayOfConnectedIpAddressVdds(
      index,
      allMatchedValues
    );
    newComponentRef.instance.setNewValue = setNewValueEmitter;
  } else {
    const newComponentRef = structureContainer.createComponent(VddIpAddressReadonlyComponent);
    newComponentRef.instance.structureArray = getArrayOfConnectedIpAddressVdds(
      index,
      allMatchedValues
    );
  }
}

export function createDiagnosticsVddMacAddressComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  index: number,
  allMatchedValues: RemoteDiagnosticsVddGenericStructure[],
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  if (isVddEditable) {
    const newComponentRef = structureContainer.createComponent(VddMacAddressEditComponent);
    newComponentRef.instance.structureArray = getArrayOfConnectedMacAddressVdds(
      index,
      allMatchedValues
    );
    newComponentRef.instance.setNewValue = setNewValueEmitter;
  } else {
    const newComponentRef = structureContainer.createComponent(VddMacAddressReadonlyComponent);
    newComponentRef.instance.structureArray = getArrayOfConnectedMacAddressVdds(
      index,
      allMatchedValues
    );
  }
}

export function createDiagnosticsVddNumberComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  diagnosticsFormGroupService: DiagnosticsFormGroupService,
  allMatchedValues: RemoteDiagnosticsVddGenericStructure[],
  metadataFlattenedValues: RemoteDiagnosticsVddGenericStructure[],
  index: number,
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  const matchedValue = allMatchedValues[index];
  if (isVddEditable) {
    const newComponentRef = structureContainer.createComponent(VddNumberEditComponent);
    newComponentRef.instance.matchedValue = matchedValue;
    newComponentRef.instance.diagnosticsFormGroupService = diagnosticsFormGroupService;
    newComponentRef.instance.metadataValues = metadataFlattenedValues;
    newComponentRef.instance.setNewValue = setNewValueEmitter;
  } else {
    const newComponentRef = structureContainer.createComponent(VddNumberReadonlyComponent);
    newComponentRef.instance.label = matchedValue?.translationid;
    newComponentRef.instance.value = matchedValue?.value;
    newComponentRef.instance.unit = matchedValue?.unit;
    newComponentRef.instance.isVersion = matchedValue?.semantic?.includes(
      SemanticValues.VERSIONVALUE
    );
    newComponentRef.instance.isNextValueVersion = isVersion(index + 1, allMatchedValues);
  }
}

export function createVddBooleanChoiceComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  matchedValue: RemoteDiagnosticsVddGenericStructure,
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  const newComponentRef = structureContainer.createComponent(VddBooleanChoiceComponent);
  newComponentRef.instance.isVddEditable = isVddEditable;
  newComponentRef.instance.matchedValue = matchedValue;
  newComponentRef.instance.setNewValue = setNewValueEmitter;
}

export function createDiagnosticsVddEnumComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  matchedValue: RemoteDiagnosticsVddGenericStructure,
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  if (isVddEditable) {
    const newComponentRef = structureContainer.createComponent(VddEnumEditComponent);
    newComponentRef.instance.matchedValue = matchedValue;
    newComponentRef.instance.setNewValue = setNewValueEmitter;
  } else {
    const newComponentRef = structureContainer.createComponent(VddEnumReadonlyComponent);
    newComponentRef.instance.label = matchedValue?.translationid;
    newComponentRef.instance.value = matchedValue?.value;
  }
}

export function createDiagnosticsTextComponent(
  structureContainer: ViewContainerRef,
  isVddEditable: boolean,
  matchedValue: RemoteDiagnosticsVddGenericStructure,
  setNewValueEmitter: EventEmitter<SetNewValueObject>
): void {
  if (isVddEditable) {
    const newComponentRef = structureContainer.createComponent(VddTextEditComponent);
    newComponentRef.instance.matchedValue = matchedValue;
    newComponentRef.instance.isDescription = matchedValue?.semantic === SemanticValues.DESCRIPTION;
    newComponentRef.instance.setNewValue = setNewValueEmitter;
  } else {
    const newComponentRef = structureContainer.createComponent(VddTextReadonlyComponent);
    newComponentRef.instance.label = matchedValue?.translationid;
    newComponentRef.instance.value = matchedValue?.value;
    if (matchedValue?.semantic === SemanticValues.DESCRIPTION) {
      const nativeElement = newComponentRef.location.nativeElement;
      nativeElement.classList.add('fill-width');
      nativeElement.classList.add('description-divider');
      newComponentRef.instance.isDescription = true;
    }
  }
}

function isVersion(index: number, matchedValues: RemoteDiagnosticsVddGenericStructure[]): boolean {
  const matchedValue = matchedValues?.[index];
  const semantic = matchedValue?.semantic;

  return semantic ? semantic.includes(SemanticValues.VERSIONVALUE) : false;
}

function getArrayOfConnectedIpAddressVdds(
  startingIndex,
  matchedValues
): RemoteDiagnosticsVddGenericStructure[] {
  return getArrayOfMatchedVdds(
    startingIndex,
    matchedValues,
    DiagnosticsHelpers.isStructureIpAddressType.bind(this)
  );
}

function getArrayOfConnectedMacAddressVdds(
  startingIndex,
  matchedValues
): RemoteDiagnosticsVddGenericStructure[] {
  return getArrayOfMatchedVdds(
    startingIndex,
    matchedValues,
    DiagnosticsHelpers.isStructureMacAddressType.bind(this)
  );
}

function getArrayOfMatchedVdds(
  startingIndex: number,
  matchedValues: RemoteDiagnosticsVddGenericStructure[],
  matchMethod: (structure: RemoteDiagnosticsVddGenericStructure) => boolean
): RemoteDiagnosticsVddGenericStructure[] {
  const result = [matchedValues[startingIndex]];
  let index = startingIndex + 1;

  while (index + 1 <= matchedValues.length) {
    if (matchMethod(matchedValues[index])) {
      matchedValues[index].skip = true;
      result.push(matchedValues[index]);
      index++;
    } else {
      break;
    }
  }

  return result;
}
